import { useQuery } from '@apollo/client';
import { products } from '@seek/sbiz-taxonomies';
import { Stack, Text, Alert, Hidden, Box, Heading } from 'braid-design-system';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Routes from 'src/config/routes';
import { GetListingProductsForPackagesPageDocument } from 'src/generated/graphql';
import { tracking } from 'src/tracking';
import { fireSelectedPrivateSellerSelectedProductLoggedInEvent } from 'src/utils/analytics';
import { validateListingProducts } from 'src/utils/listingProducts';

import { DesktopPackageColumns } from './components/DesktopPackageColumns';
import { FreeToPaidUpsellDialog } from './components/FreeToPaidUpsellDialog';
import { HeaderMessage } from './components/HeaderMessage';
import { MobilePackageTiles } from './components/MobilePackageTiles';

export const PackagesContainer = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const listingId = searchParams.get('listingId');

  const [isFreeToPaidUpsellDialogOpen, setIsFreeToPaidUpsellDialogOpen] =
    useState(false);

  const { loading, data, error } = useQuery(
    GetListingProductsForPackagesPageDocument,
  );

  if (loading) {
    return <CustomLoader />;
  }

  if (error || !data || data.listingProducts.length === 0) {
    return (
      <Alert
        tone="critical"
        data={{ testid: 'get-listing-products-error-notice' }}
      >
        <Text>
          There was an error getting packages. Please try again later.
        </Text>
      </Alert>
    );
  }

  const validatedProducts = validateListingProducts(data.listingProducts);

  if (validatedProducts.kind === 'error') {
    return (
      <Alert tone="critical" data={{ testid: 'product-validation-error' }}>
        <Text>
          There was an error getting packages. Please try again later.
        </Text>
      </Alert>
    );
  }

  const { accelerate, freemium, standard } = validatedProducts.singleProducts;

  const navigateToFreemiumContract = () => {
    const newSearchParams = new URLSearchParams();
    if (listingId) {
      newSearchParams.set('listingId', listingId);
    }

    navigate(`${Routes.FREEMIUM_CONTRACT}?${newSearchParams.toString()}`);
  };

  const handleFreeClick = () => {
    fireSelectedPrivateSellerSelectedProductLoggedInEvent(freemium);
    setIsFreeToPaidUpsellDialogOpen(true);
  };

  const handleStandardClick = () => {
    fireSelectedPrivateSellerSelectedProductLoggedInEvent(standard);

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('productToken', standard.productToken);
    if (listingId) {
      newSearchParams.set('listingId', listingId);
    }

    navigate(`${Routes.PAYMENT}?${newSearchParams.toString()}`);
  };

  const handleAccelerateClick = () => {
    fireSelectedPrivateSellerSelectedProductLoggedInEvent(accelerate);

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('productToken', accelerate.productToken);
    if (listingId) {
      newSearchParams.set('listingId', listingId);
    }

    navigate(`${Routes.PAYMENT}?${newSearchParams.toString()}`);
  };

  return (
    <Stack space="large">
      <HeaderMessage />

      <Heading level="2">Choose the right selling approach for you</Heading>

      <Hidden below="desktop">
        <Box data={{ testid: 'desktop-package-columns' }}>
          <DesktopPackageColumns
            onFreemiumClick={handleFreeClick}
            onStandardClick={handleStandardClick}
            onAccelerateClick={handleAccelerateClick}
            htags={htags}
          />
        </Box>
      </Hidden>

      <Hidden above="tablet">
        <Box data={{ testid: 'mobile-package-tiles' }}>
          <MobilePackageTiles
            onFreemiumClick={handleFreeClick}
            onStandardClick={handleStandardClick}
            onAccelerateClick={handleAccelerateClick}
            htags={htags}
          />
        </Box>
      </Hidden>

      <FreeToPaidUpsellDialog
        isOpen={isFreeToPaidUpsellDialogOpen}
        onClose={() => {
          tracking.packages.freeToPaidUpsellPrompt.cancelled();
          setIsFreeToPaidUpsellDialogOpen(false);
        }}
        onFreeClick={() => {
          tracking.packages.freeToPaidUpsellPrompt.freeSelected();
          navigateToFreemiumContract();
        }}
        onCancelClick={() => {
          tracking.packages.freeToPaidUpsellPrompt.paidSelected();
          setIsFreeToPaidUpsellDialogOpen(false);
        }}
      />
    </Stack>
  );
};

const htags = {
  freemium: products.Freemium.productCode,
  standard: products.Standard.productCode,
  accelerate: products.Accelerate.productCode,
};
